import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import qs from 'qs';
import AuthenticationModule from '@/store/modules/authentication.module';
import { getModule } from 'vuex-module-decorators';
import store from '@/store';
import { REFRESH_TOKEN_URL } from './authentication.service';
import router from '@/router';
import { routeName } from '@/constants/route.constants';
import { AuthenticationActions } from '@/constants/actions.constants';
import { injectable } from 'inversify-props';

export interface BaseAPIService {
  setJWT(jwt: string): void;
  unsetJWT(): void;
}

@injectable()
export class BaseAPIService implements BaseAPIService {
  public readonly client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: process.env.VUE_APP_API_BASE_URL,
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json'
      },
      paramsSerializer(params) {
        return qs.stringify(params);
      }
    });

    // request interceptor
    this.client.interceptors.request.use(
      (config) => {
        // console.log('params');
        // console.log(config.params);

        // console.log('data');
        // console.log(config.data);

        return config;
      },
      (error) => {
        console.error('base api service request error');
        console.error(error);

        return Promise.reject(error);
      }
    );

    // response interceptor
    this.client.interceptors.response.use(
      (response) => {
        // Any status code that lie within the range of 2xx cause this function to trigger
        // console.log('response');
        // console.log(response);

        return response;
      },
      async (error) => {
        // Any status codes that falls outside the range of 2xx cause this function to trigger
        const originalRequest: AxiosRequestConfig & {
          _retry: boolean;
        } = error.config;

        const authenticationModule: AuthenticationModule = getModule(
          AuthenticationModule,
          store
        );

        if (error.response.status >= 400 && error.response.status <= 499) {
          // handle individual cases in this block

          if (originalRequest.url === REFRESH_TOKEN_URL) {
            await router.replace({ name: routeName.login });
            throw error;
          }

          if (error.response.status === 403) {
            error.response.data = {
              errors: ['Forbidden.']
            };
            throw error;
          }

          // don't throw error here
        }

        if (error.response.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          await authenticationModule[
            AuthenticationActions.refreshTokenAction
          ]();

          originalRequest.headers[
            'Authorization'
          ] = `Bearer ${authenticationModule.loginResponse?.token}`;

          return await this.client(originalRequest);
        }

        throw error;
      }
    );
  }

  public setJWT(jwt: string): void {
    this.client.defaults.headers = {
      ...this.client.defaults.headers,
      Authorization: 'Bearer ' + jwt
    };
  }

  public unsetJWT(): void {
    if (this.client.defaults.headers['Authorization']) {
      delete this.client.defaults.headers['Authorization'];
    }
  }
}
